import { IconFont } from '@byecode/ui'
import type { DataSourceAbstract, Field, RecordLikeProtocol } from '@lighthouse/core'
import { reduce } from 'rambda'
import React, { useMemo } from 'react'

import { CURRENT_USER, USER_DATASOURCE } from '../../../../constants'
import { SelectPlaceHolder } from '../../SelectPlaceHolder'
import * as CM from '../commonStyles'
import { RelationNormalTag } from './RelationNormalTag'
import * as SC from './styles'

interface RelationRecordPreviewProps {
    readOnly?: boolean
    value: string[]
    dsId: string
    relativeField: Field | undefined
    relativeShowField: Field | undefined
    userDataSource: DataSourceAbstract | undefined
    userRecord?: RecordLikeProtocol
    placeholder?: string
    placeholderColor?: string

    onClick?: React.MouseEventHandler<HTMLButtonElement>
    onRemove: (value: string) => void
}

export const RelationRecordPreview = React.forwardRef<HTMLButtonElement, RelationRecordPreviewProps>(
    (
        {
            readOnly,
            value,
            dsId,
            relativeField,
            relativeShowField,
            userDataSource,
            userRecord,
            placeholder,
            placeholderColor,
            onClick,
            onRemove
        },
        ref
    ) => {
        const [tagList, userTag] = useMemo(() => {
            return reduce<string, [string[], string | null]>(
                ([tagList, CurrentUser], curVal) => {
                    if (curVal.includes(CURRENT_USER.id)) {
                        return [tagList, curVal]
                    }
                    return [[...tagList, curVal], CurrentUser]
                },
                [[], null],
                value
            )
        }, [value])

        const relationTagsEle = useMemo(() => {
            if (!dsId || !relativeField || !relativeShowField) {
                return null
            }

            return tagList.map(recordId => {
                return (
                    <RelationNormalTag
                        source="input"
                        dsId={dsId}
                        relativeField={relativeField}
                        relativeShowField={relativeShowField}
                        enableDelete={!readOnly}
                        disabled={readOnly}
                        key={recordId}
                        recordId={recordId}
                        onRemove={() => onRemove(recordId)}
                    />
                )
            })
        }, [dsId, relativeField, relativeShowField, tagList, readOnly, onRemove])

        const userTagEle = useMemo(() => {
            if (!userDataSource || !userRecord || !userTag) {
                return null
            }
            const { schema } = userDataSource
            const relativeField = schema?.[userTag.replace(`${CURRENT_USER.id}-`, '')]

            return (
                <RelationNormalTag
                    source="input"
                    dsId={USER_DATASOURCE}
                    relativeField={relativeField}
                    relativeShowField={relativeField}
                    enableDelete={!readOnly}
                    disabled={readOnly}
                    recordId={userRecord.id}
                    onRemove={() => onRemove(userTag)}
                />
            )
        }, [onRemove, readOnly, userDataSource, userRecord, userTag])

        return (
            <SC.Relation type="button" onClick={onClick} isWrap ref={ref}>
                <SC.TagContain>
                    {userTagEle}
                    {relationTagsEle}
                    {value.length === 0 && (
                        <SelectPlaceHolder color={placeholderColor} style={{ lineHeight: '28px', height: '100%' }} label={placeholder} />
                    )}
                </SC.TagContain>
                <CM.IconWrapper data-field-icon style={{ height: 28 }}>
                    {!readOnly && <IconFont type="ArrowDownSmall" size={16} />}
                </CM.IconWrapper>
            </SC.Relation>
        )
    }
)
